import { ChevronDown, Minus, Plus } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { Serving } from '../../shared/foods';
import {
  amountIn,
  gramsPerUnit,
  kitchenReadout,
  parseTypedAmount,
  stepAmount,
  unitAmountText,
  unitName,
  unitsFor,
  unitSymbol,
  type AmountUnit,
} from '../../shared/kitchenMeasures';
import { useKitchenUnits } from '../api/hooks';
import { readAmountUnit, writeAmountUnit } from '../lib/amountUnits';
import { clampGrams, MAX_GRAMS, sizeKcal, sizeLabel, stepDown, stepUp, tenth } from '../lib/amounts';
import { kcal } from '../lib/format';
import { SizeChips, type SizeChip } from './SizeChips';

interface AmountProps {
  value: number;
  onChange: (grams: number) => void;
  servings?: Serving[];
  liquid?: boolean;
  label?: string;
  /** The food's calories per 100 g (or ml): each size then shows its own calories. Leave out to show sizes without them. */
  kcalPer100?: number | null;
  /** The amount logged last time, offered first as "Last time · 65 g". */
  lastGrams?: number | null;
  /** The food's ref ("cat:butter", "fs:…", "recipe:…"): the unit last chosen for it is remembered on this device. */
  foodRef?: string | null;
  /** The food's name, which some typed amounts need ("1 stick" is butter's). */
  name?: string;
}

/**
 * Both or neither. With them, the unit is the caller's (a recipe ingredient keeps its own): the
 * field shows it when the food can convert it, and the device's memory is neither read nor written.
 */
type UnitProps = { unit?: undefined; onUnitChange?: undefined } | { unit: AmountUnit; onUnitChange: (unit: AmountUnit) => void };

type GramsInputProps = AmountProps & UnitProps;

/** Sizes shown before "+N more". */
const SIZE_LIMIT = 4;

/** One-tap multiples of whatever amount is set: two containers of yogurt is "1 container", then 2×. */
const MULTIPLIERS = [
  { factor: 0.5, text: '½×', label: 'Halve the amount' },
  { factor: 2, text: '2×', label: 'Double the amount' },
  { factor: 3, text: '3×', label: 'Triple the amount' },
] as const;

const matches = (grams: number, value: number) => Math.abs(grams - value) < 0.5;

/**
 * Grams are the base unit: a stepper (1 g at a time), one-tap sizes with their calories, quick
 * multiples, and under the stepper what the amount is in the kitchen ("≈ 1⅔ cups"). The unit
 * button switches the field to cups, spoons or ounces (ml in metric) where the food can convert
 * them, which is how a phone's number keypad gets kitchen units; typed units ("1 1/2 cups")
 * work too. The unit chosen is remembered for the food on this device, unless the caller keeps it
 * (unit and onUnitChange, as each recipe ingredient does). Whatever the field shows, onChange gets grams.
 */
export function GramsInput({
  value,
  onChange,
  servings = [],
  liquid,
  label = 'Amount',
  kcalPer100,
  lastGrams,
  foodRef,
  unit: unitProp,
  onUnitChange,
  name,
}: GramsInputProps) {
  const kitchenUnits = useKitchenUnits();
  // Callers may pass a new servings array each render: the food only changes when its sizes do.
  const sizesKey = servings.map((s) => `${s.label}:${s.grams}`).join('|');
  const food = useMemo(() => ({ servings, liquid, name }), [sizesKey, liquid, name]);
  const controlled = onUnitChange !== undefined;
  const units = useMemo(() => {
    const offered = unitsFor(food, kitchenUnits);
    // A caller's unit outside these measures (cups saved in a recipe, opened in metric) is offered too
    // when the food can convert it: the field then says what the recipe does, and can be switched away from it.
    const extra = controlled && unitProp !== undefined && unitProp !== 'g' && !offered.includes(unitProp) && gramsPerUnit(unitProp, food) !== null;
    return extra ? [...offered, unitProp] : offered;
  }, [food, kitchenUnits, controlled, unitProp]);

  const remembered = () => (foodRef && !controlled ? readAmountUnit(foodRef) : null) ?? 'g';
  const [picked, setPicked] = useState<AmountUnit>(remembered);
  // After a step or a typed number, the field keeps the number meant rather than the one that
  // grams kept to 0.1 g work back to (1.3 tsp of a light herb, not 1.29), while the grams are
  // still the ones it made. Other amounts show on a whole step when they're within rounding of one.
  const [meant, setMeant] = useState<{ unit: AmountUnit; grams: number; amount: number } | null>(null);
  // What's being typed, until it's committed. Otherwise the field shows the amount in its unit,
  // worked out as it renders, so a new amount or unit never shows the old text for a moment.
  const [draft, setDraft] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);

  // Another food in the same control (Replace, Undo) starts afresh, in the unit chosen for it.
  const foodKey = `${foodRef ?? ''}|${sizesKey}`;
  const [shownFor, setShownFor] = useState(foodKey);
  if (shownFor !== foodKey) {
    setShownFor(foodKey);
    setPicked(remembered());
    setMeant(null);
    setDraft(null);
    setNote(null);
    setChoosing(false);
  }
  // A unit this food can't convert (or not in these measures) shows grams, and stays remembered (or the caller's).
  const wanted = controlled ? (unitProp ?? 'g') : picked;
  const unit: AmountUnit = units.includes(wanted) ? wanted : 'g';

  const amountOf = (grams: number): number => {
    if (unit === 'g') return tenth(grams);
    if (meant && meant.unit === unit && meant.grams === grams) return meant.amount;
    return amountIn(grams, unit, food) ?? grams;
  };
  const textFor = (grams: number) => (unit === 'g' ? String(tenth(grams)) : unitAmountText(amountOf(grams)));
  const shown = textFor(value);
  const text = draft ?? shown;

  const set = (grams: number) => {
    setNote(null);
    onChange(grams);
  };

  const commit = (raw: string) => {
    setDraft(null);
    // Leaving the field as it was changes nothing (its text may be rounded: 1.59 cups).
    if (raw.trim() === shown) return;
    const read = parseTypedAmount(raw, food, unit);
    if (!read.ok) return setNote(read.note);
    setMeant(read.amount !== null && unit !== 'g' ? { unit, grams: read.grams, amount: read.amount } : null);
    set(read.grams);
  };

  const step = (direction: 1 | -1) => {
    if (unit === 'g') return set(direction > 0 ? stepUp(value) : stepDown(value));
    const next = stepAmount(amountOf(value), unit, direction, food);
    setNote(null);
    if (!next) return;
    setMeant({ unit, grams: next.grams, amount: next.amount });
    onChange(next.grams);
  };

  const unitButton = useRef<HTMLButtonElement>(null);
  const chooser = useRef<HTMLDivElement>(null);
  const chooserId = useId();
  const readoutId = useId();
  // Opening the choices moves focus to the unit in use, so the keyboard (and Escape) start there.
  useEffect(() => {
    if (choosing) chooser.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
  }, [choosing]);
  const closeChoices = () => {
    setChoosing(false);
    unitButton.current?.focus();
  };
  const pick = (u: AmountUnit) => {
    if (onUnitChange) onUnitChange(u);
    else {
      setPicked(u);
      if (foodRef) writeAmountUnit(foodRef, u);
    }
    setMeant(null);
    setDraft(null);
    setNote(null);
    closeChoices();
  };
  /** "Unit: cups", or with the symbol shown when the name differs from it: "Unit: tbsp (tablespoons)". */
  const unitLabel = (u: AmountUnit) => {
    const symbol = unitSymbol(u, liquid);
    const name = unitName(u, liquid);
    return symbol === name ? name : `${symbol} (${name})`;
  };

  // The readout follows what's being typed, before it's committed.
  const typed = draft === null || draft.trim() === shown ? null : parseTypedAmount(draft, food, unit);
  const shownGrams = typed?.ok ? typed.grams : value;
  const readout =
    unit === 'g'
      ? kitchenReadout(shownGrams, food, kitchenUnits)
      : `= ${shownGrams.toLocaleString('en-US', { maximumFractionDigits: 1 })} ${unitSymbol('g', liquid)}`;
  const shownAmount = typed?.ok && typed.amount !== null ? typed.amount : amountOf(value);

  const base = unitSymbol('g', liquid);
  const chips: SizeChip[] = servings.map((s, i) => {
    const sizeCalories = sizeKcal(s.grams, kcalPer100);
    return {
      key: `${i}:${s.label}:${s.grams}`,
      label: sizeLabel(s, liquid),
      detail: sizeCalories === null ? null : `${kcal(sizeCalories)} kcal`,
      active: matches(s.grams, value),
      onPick: () => set(s.grams),
    };
  });
  if (lastGrams) {
    chips.unshift({
      key: 'last',
      label: 'Last time',
      detail: `${tenth(lastGrams)} ${base}`,
      lead: true,
      active: matches(lastGrams, value),
      onPick: () => set(lastGrams),
    });
  }
  return (
    <div className="stack" style={{ '--gap': '8px' } as React.CSSProperties}>
      <div className="row wrap grams-row">
        <div
          className="grams-col"
          onKeyDown={
            choosing
              ? (e) => {
                  // While the units are open, Escape closes them (from the button too), never the sheet around them.
                  if (e.key !== 'Escape') return;
                  e.stopPropagation();
                  closeChoices();
                }
              : undefined
          }
        >
          <div className="grams" role="group" aria-label={label}>
            <button type="button" className="grams__btn" aria-label="Less" onClick={() => step(-1)}>
              <Minus size={16} />
            </button>
            <input
              className="grams__input num"
              inputMode="decimal"
              aria-label={`${label} in ${unitName(unit, liquid)}`}
              aria-describedby={readoutId}
              value={text}
              style={{ '--chars': Math.min(10, Math.max(text.length, 1)) } as React.CSSProperties}
              onChange={(e) => {
                setDraft(e.target.value);
                setNote(null);
              }}
              onBlur={(e) => commit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
              }}
            />
            {units.length > 1 ? (
              <button
                ref={unitButton}
                type="button"
                className="grams__unit grams__unit--pick"
                aria-label={`Unit: ${unitLabel(unit)}`}
                aria-expanded={choosing}
                aria-controls={choosing ? chooserId : undefined}
                onClick={() => setChoosing((open) => !open)}
              >
                {unitSymbol(unit, liquid, shownAmount)}
                <ChevronDown size={12} aria-hidden="true" />
              </button>
            ) : (
              <span className="grams__unit">{base}</span>
            )}
            <button type="button" className="grams__btn" aria-label="More" onClick={() => step(1)}>
              <Plus size={16} />
            </button>
          </div>
          {choosing && (
            <div ref={chooser} id={chooserId} className="servings grams__units" role="radiogroup" aria-label={`${label}: unit`}>
              {units.map((u) => {
                const symbol = unitSymbol(u, liquid);
                const name = unitName(u, liquid);
                return (
                  <button key={u} type="button" role="radio" aria-checked={u === unit} className={`chip chip--sm${u === unit ? ' is-active' : ''}`} onClick={() => pick(u)}>
                    {symbol}
                    {name !== symbol && <span className="sr-only"> ({name})</span>}
                  </button>
                );
              })}
            </div>
          )}
          {/* Busy while typing: the readout follows each keystroke, but is announced once the amount is entered. */}
          <p
            id={readoutId}
            className={`grams__readout num${note ? ' grams__readout--note' : ''}`}
            aria-live="polite"
            aria-busy={draft !== null || undefined}
          >
            {note ?? readout}
          </p>
        </div>
        {/* A different food (say after Replace) starts with its sizes folded again. */}
        <SizeChips key={sizesKey} chips={chips} limit={SIZE_LIMIT + (lastGrams ? 1 : 0)} label={`${label}: sizes`} />
      </div>
      <div className="row wrap mults">
        <div className="servings" role="group" aria-label={`${label}: quick multiples`}>
          {MULTIPLIERS.map((m) => (
            <button
              type="button"
              key={m.factor}
              className="chip chip--sm"
              aria-label={m.label}
              disabled={m.factor > 1 ? value >= MAX_GRAMS : value <= 1}
              onClick={() => set(clampGrams(value * m.factor))}
            >
              {m.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
